import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If the email exists, a reset link was sent (check server logs in demo mode).");
      nav("/login");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md rounded-xl border border-border p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-md bg-primary/10 p-2 text-primary"><KeyRound className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Forgot password</h1>
            <p className="text-sm text-muted-foreground">Enter your email to receive a reset link.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit">Send reset link</Button>
          <div className="text-center text-xs">
            <Link to="/login" className="text-primary hover:underline">Back to login</Link>
          </div>
        </form>
      </Card>
    </div>
  );
}

export function ChangePasswordPage() {
  const [oldPwd, setOld] = useState("");
  const [newPwd, setNew] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      toast.success("Password changed");
      nav("/dashboard");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg py-6">
      <Card className="rounded-xl border border-border p-8 shadow-sm">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold tracking-tight">Change password</h1>
          <p className="mt-1 text-sm text-muted-foreground">Update your account password.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" required value={oldPwd} onChange={(e) => setOld(e.target.value)} data-testid="cp-old" />
          </div>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input type="password" required minLength={6} value={newPwd} onChange={(e) => setNew(e.target.value)} data-testid="cp-new" />
          </div>
          <Button type="submit" disabled={loading} data-testid="cp-submit">Save</Button>
        </form>
      </Card>
    </div>
  );
}
